import { useState, useEffect } from 'react'
import { Share, Plus, X, Download } from 'lucide-react'

// Chrome/Android fire this before showing their own install UI; we capture it
// to offer a one-tap install button. iOS Safari has no equivalent event —
// there install is always manual via the Share sheet, so we show instructions.
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const DISMISS_KEY = 'trackmind.installHintDismissed'

function isStandalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    // iOS Safari exposes this non-standard flag when launched from home screen
    (window.navigator as { standalone?: boolean }).standalone === true
  )
}

function isIOS(): boolean {
  const ua = window.navigator.userAgent
  const iOSDevice = /iphone|ipad|ipod/i.test(ua)
  // iPadOS 13+ reports as Mac; detect via touch support
  const iPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1
  return iOSDevice || iPadOS
}

/**
 * A dismissable "install this app" hint shown only when the app is running in
 * a normal browser tab (not already installed) and the user hasn't dismissed
 * it before. On Android it offers a real install button; on iOS it shows the
 * manual Share → Add to Home Screen instructions (Apple allows nothing else).
 */
export function InstallPrompt() {
  const [visible, setVisible] = useState(false)
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [ios, setIos] = useState(false)

  useEffect(() => {
    // Never nudge if already installed or previously dismissed
    if (isStandalone()) return
    try {
      if (localStorage.getItem(DISMISS_KEY)) return
    } catch { /* localStorage blocked — treat as not dismissed */ }

    if (isIOS()) {
      setIos(true)
      setVisible(true)
      return
    }

    // Android / desktop Chrome path: wait for the browser's install signal
    const handler = (e: Event) => {
      e.preventDefault() // stop Chrome's default mini-infobar
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      setVisible(true)
    }
    window.addEventListener('beforeinstallprompt', handler)

    // If the app gets installed, hide immediately
    const installedHandler = () => setVisible(false)
    window.addEventListener('appinstalled', installedHandler)

    return () => {
      window.removeEventListener('beforeinstallprompt', handler)
      window.removeEventListener('appinstalled', installedHandler)
    }
  }, [])

  const dismiss = () => {
    setVisible(false)
    try { localStorage.setItem(DISMISS_KEY, '1') } catch { /* ignore */ }
  }

  const install = async () => {
    if (!deferredPrompt) return
    await deferredPrompt.prompt()
    await deferredPrompt.userChoice
    setDeferredPrompt(null)
    dismiss()
  }

  if (!visible) return null

  return (
    <div className="fixed inset-x-0 bottom-[calc(3.75rem+env(safe-area-inset-bottom))] z-[9995] px-3 pb-2 md:bottom-4 md:left-auto md:right-4 md:max-w-sm md:px-0">
      <div className="rounded-xl border bg-card shadow-2xl">
        <div className="flex items-start gap-3 p-3">
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Download className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold">Install TrackMind</p>
            {ios ? (
              <p className="mt-0.5 text-xs text-muted-foreground">
                Tap{' '}
                <Share className="inline h-3.5 w-3.5 -translate-y-0.5" aria-label="the Share button" />
                {' '}in the toolbar, then{' '}
                <span className="whitespace-nowrap font-medium text-foreground">
                  Add to Home Screen{' '}
                  <Plus className="inline h-3 w-3 -translate-y-0.5" />
                </span>
                {' '}— it launches full-screen and works offline.
              </p>
            ) : (
              <p className="mt-0.5 text-xs text-muted-foreground">
                Add it to your home screen for a full-screen, offline-capable app.
              </p>
            )}
            {!ios && deferredPrompt && (
              <button
                onClick={install}
                className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
              >
                <Download className="h-3.5 w-3.5" /> Install
              </button>
            )}
          </div>
          <button
            onClick={dismiss}
            className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
